'use client';

import Link from 'next/link';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { Box, Typography } from '@mui/material';

import { useAuth } from '@/hooks';
import { useStreakStatus } from '@/services/query/streak.queries';

import { ROUTES } from '@/constants';

/**
 * Persistent streak progress pill — mirrors the styling of PortfolioBalances.
 * Shows today's volume progress toward the current tier target ("$35/$50
 * today") with a thin fill bar underneath. Desktop-only, like its neighbor.
 *
 * Mutually exclusive with StreakBanner: while the top banner is showing
 * (bannerDismissed === false), this pill stays hidden so the two never
 * appear together. It only takes over as the entry point to /streaks once
 * the user dismisses the banner.
 */
export const StreakProgressPill = () => {
  const { isAuthenticated } = useAuth();
  const { data } = useStreakStatus(isAuthenticated);

  if (!isAuthenticated || !data?.bannerDismissed) return null;

  return (
    <Link href={ROUTES.STREAKS} style={{ textDecoration: 'none' }}>
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 0.5,
          mr: { md: 0.5, lg: 1 },
          px: { md: 1.5, lg: 2 },
          py: { md: 0.75, lg: 1 },
          minWidth: { md: 110, lg: 130 },
          borderRadius: '12px',
          bgcolor: 'background.subtle',
          border: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: 'background.hover',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LocalFireDepartmentIcon
            sx={{ fontSize: { md: '1rem', lg: '1.125rem' }, color: '#e8b046' }}
          />
          <Typography
            sx={{
              fontSize: { md: '0.6875rem', lg: '0.75rem' },
              fontWeight: 700,
              color: 'text.primary',
              whiteSpace: 'nowrap',
            }}
          >
            ${data.todaysVolume.toFixed(0)}/${data.todaysTarget.toFixed(0)} today
          </Typography>
        </Box>
        <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'action.hover', overflow: 'hidden' }}>
          <Box
            sx={{
              height: '100%',
              width: `${data?.todaysProgressPct ?? 0}%`,
              borderRadius: 2,
              background: 'linear-gradient(90deg, #e8b046, #e0664f)',
              transition: 'width 0.6s ease',
            }}
          />
        </Box>
      </Box>
    </Link>
  );
};
